import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import { useOpsMonitor } from './Index'
import InfoTip from '../../../components/InfoTip'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return '—'
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60)   return `${sec}s ago`
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`
  return `${Math.round(sec / 3600)}h ago`
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Congestion color scale: matches ChannelSensorCard in mobile app
function congestionColor(idx) {
  if (idx >= 0.8) return '#F44336'  // critical red
  if (idx >= 0.6) return '#FF9800'  // high amber
  if (idx >= 0.4) return '#FFC107'  // moderate yellow
  return '#4CAF50'                   // low green
}

function congestionLabel(idx) {
  if (idx >= 0.8) return 'CRITICAL'
  if (idx >= 0.6) return 'HIGH'
  if (idx >= 0.4) return 'MODERATE'
  return 'LOW'
}

const IROP_COLORS  = { high: '#F44336', moderate: '#FF9800', low: '#4CAF50' }
const ANCHOR_ICONS = { gate: '🚪', lounge: '🛋', security: '🔒', baggage: '🧳', restaurant: '🍽', corridor: '📍', default: '📍' }

// ─── Congestion bar ───────────────────────────────────────────────────────────

function CongestionBar({ value }) {
  const color = congestionColor(value)
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.round(value * 100)}%`, background: color }}
        />
      </div>
      <span
        className="text-xs font-bold tabular-nums w-10 text-right"
        style={{ color }}
      >
        {Math.round(value * 100)}%
      </span>
    </div>
  )
}

// ─── Venue tile ───────────────────────────────────────────────────────────────

function VenueTile({ tile, onSelect }) {
  const congestionIdx = tile.congestion_index || 0
  const iropColor     = IROP_COLORS[tile.irop_potential] || '#4CAF50'
  const icon          = ANCHOR_ICONS[tile.anchor_type] || ANCHOR_ICONS.default

  const borderColor = tile.irop_potential === 'high'
    ? '#F44336'
    : tile.irop_potential === 'moderate'
    ? '#FF9800'
    : '#E5E7EB'

  return (
    <button
      onClick={() => onSelect(tile)}
      className="bg-white rounded-lg border-2 p-4 text-left hover:shadow-md transition-all w-full"
      style={{ borderColor }}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg flex-shrink-0">{icon}</span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{tile.anchor_label}</p>
            <p className="text-xs text-gray-500">
              {[tile.terminal, tile.floor_level, tile.gate_ref].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
        {/* IROP badge */}
        {tile.irop_potential !== 'low' && (
          <span
            className="flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-full"
            style={{ background: iropColor + '22', color: iropColor }}
          >
            ⚠ IROP {tile.irop_potential}
          </span>
        )}
      </div>

      {/* Congestion bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Congestion</span>
          <span
            className="text-xs font-bold"
            style={{ color: congestionColor(congestionIdx) }}
          >
            {congestionLabel(congestionIdx)}
          </span>
        </div>
        <CongestionBar value={congestionIdx} />
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        {/* Friction */}
        <div>
          <span className="text-gray-400">Friction</span>
          <div className="flex items-center gap-1 mt-0.5">
            <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.min(tile.friction_score * 50, 100)}%`, background: tile.friction_score > 1.2 ? '#F44336' : tile.friction_score > 0.8 ? '#FF9800' : '#4CAF50' }}
              />
            </div>
            <span className="tabular-nums font-bold text-gray-700">{tile.friction_score?.toFixed(1) || '—'}</span>
          </div>
        </div>

        {/* Dwell time */}
        <div>
          <span className="text-gray-400">Avg Dwell</span>
          <p className="font-semibold text-gray-700 tabular-nums">
            {tile.avg_dwell_seconds ? `${Math.round(tile.avg_dwell_seconds / 60)}min` : '—'}
          </p>
        </div>

        {/* Foot traffic */}
        <div>
          <span className="text-gray-400">Foot Traffic</span>
          <p className="font-semibold text-gray-700 tabular-nums">
            {tile.foot_traffic_rate ? `${tile.foot_traffic_rate}/min` : '—'}
          </p>
        </div>

        {/* High-risk count */}
        <div>
          <span className="text-gray-400">High Risk PAX</span>
          <p
            className="font-bold tabular-nums"
            style={{ color: tile.nearby_high_risk > 0 ? '#F44336' : '#4CAF50' }}
          >
            {tile.nearby_high_risk ?? 0}
          </p>
        </div>
      </div>

      {/* Venue status */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
        <span
          className="text-xs font-semibold capitalize"
          style={{ color: tile.venue_status === 'active' ? '#4CAF50' : tile.venue_status === 'congested' ? '#F44336' : '#9E9E9E' }}
        >
          {tile.venue_status || 'unknown'}
        </span>
        <span className="text-xs text-gray-400">{timeAgo(tile.captured_at)}</span>
      </div>
    </button>
  )
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

function TileDetailPanel({ tile, onClose }) {
  const [sending, setSending] = useState(false)
  const [msgText, setMsgText] = useState('')
  const [msgResult, setMsgResult] = useState(null)

  if (!tile) return null

  function sendAlert() {
    if (!msgText.trim() || sending) return
    setSending(true)
    fetch('/admin/cloudtraq_gds/message', {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
      },
      body: JSON.stringify({ body: msgText, pnr_locator: null }),
    })
      .then(r => r.json())
      .then(data => { setMsgResult(data.success ? 'Sent ✓' : 'Error'); setSending(false) })
      .catch(() => { setMsgResult('Error'); setSending(false) })
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900">{tile.anchor_label}</h3>
          <p className="text-xs text-gray-500">{tile.anchor_type} · {[tile.terminal, tile.floor_level].filter(Boolean).join(' · ')}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-lg">✕</button>
      </div>

      {/* Full snapshot */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Congestion',    value: tile.congestion_index ? `${Math.round(tile.congestion_index * 100)}%` : '—', color: congestionColor(tile.congestion_index || 0) },
          { label: 'Friction Score',value: tile.friction_score?.toFixed(2) || '—', color: tile.friction_score > 1.2 ? '#F44336' : '#4CAF50' },
          { label: 'Foot Traffic',  value: tile.foot_traffic_rate ? `${tile.foot_traffic_rate}/min` : '—', color: '#607D8B' },
          { label: 'Avg Dwell',     value: tile.avg_dwell_seconds ? `${Math.round(tile.avg_dwell_seconds)}s` : '—', color: '#607D8B' },
          { label: 'High Risk PAX', value: tile.nearby_high_risk ?? 0, color: tile.nearby_high_risk > 0 ? '#F44336' : '#4CAF50' },
          { label: 'IROP Potential',value: tile.irop_potential || '—', color: IROP_COLORS[tile.irop_potential] || '#9E9E9E' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-gray-50 rounded p-3 text-center">
            <p className="text-lg font-bold tabular-nums" style={{ color }}>{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Links */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <a href="/admin/cloudtraq_gds/high_risk" className="text-xs px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded hover:bg-red-100">
          View High Risk PAX
        </a>
        <a href="/admin/cloudtraq_gds/dispatch_board" className="text-xs px-3 py-1.5 bg-orange-50 border border-orange-200 text-orange-700 rounded hover:bg-orange-100">
          Dispatch Board
        </a>
      </div>

      {/* Quick CloudMist broadcast */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Send venue alert (CloudMist)</p>
        <div className="flex gap-2">
          <input
            value={msgText}
            onChange={e => setMsgText(e.target.value)}
            placeholder="Alert message for this venue…"
            className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs"
          />
          <button
            onClick={sendAlert}
            disabled={sending || !msgText.trim()}
            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-40"
          >
            {sending ? '…' : 'Send'}
          </button>
        </div>
        {msgResult && <p className="text-xs mt-1" style={{ color: msgResult.includes('✓') ? '#4CAF50' : '#F44336' }}>{msgResult}</p>}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ChannelSensorOps({ tiles, refreshed_at, tenant_branding }) {
  const intervalRef            = useRef(null)
  const [tilesState, setTiles] = useState(tiles)
  const [selected, setSelected]= useState(null)
  const [filterIrop, setFilterIrop] = useState('all')  // 'all' | 'high' | 'moderate'
  const [lastRefresh, setLastRefresh] = useState(refreshed_at)
  const [wsUpdateCount, setWsUpdateCount] = useState(0)

  // Sync on prop change
  useEffect(() => { setTiles(tiles) }, [tiles])
  useEffect(() => { setLastRefresh(refreshed_at) }, [refreshed_at])

  // Auto-reload every 2 minutes (channelSensor job runs every 15min; interim updates via WS)
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      router.reload({ only: ['tiles', 'refreshed_at'] })
    }, 120_000)
    return () => clearInterval(intervalRef.current)
  }, [])

  // Real-time CHANNEL_SENSOR_UPDATE from OpsMonitorChannel
  useOpsMonitor({
    tenantKey: tenant_branding?.tenant_key || 'cloudtraq',
    onEvent: (event) => {
      if (event === 'CHANNEL_SENSOR_UPDATE') {
        router.reload({ only: ['tiles', 'refreshed_at'] })
        setWsUpdateCount(n => n + 1)
      }
    },
  })

  // Filter
  const visibleTiles = tilesState.filter(t => {
    if (filterIrop === 'all') return true
    return t.irop_potential === filterIrop
  })

  // Sort: IROP high first, then by congestion desc
  const sortedTiles = [...visibleTiles].sort((a, b) => {
    const iropOrder = { high: 0, moderate: 1, low: 2 }
    const ia = iropOrder[a.irop_potential] ?? 2
    const ib = iropOrder[b.irop_potential] ?? 2
    if (ia !== ib) return ia - ib
    return (b.congestion_index || 0) - (a.congestion_index || 0)
  })

  const highRiskCount = tilesState.filter(t => t.irop_potential === 'high').length
  const criticalCount = tilesState.filter(t => (t.congestion_index || 0) >= 0.8).length

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">channelSensor Ops</h1>
            <p className="text-sm text-gray-500 mt-1">
              Ground-truth AVA insight snapshots · {tilesState.length} venues ·
              refreshed {timeAgo(lastRefresh)}
              {wsUpdateCount > 0 && <span className="ml-1.5 text-blue-600">· {wsUpdateCount} live updates</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => router.reload()} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
          </div>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Total Venues',     value: tilesState.length,   color: '#607D8B' },
            { label: 'Critical Congestion', value: criticalCount,     color: criticalCount > 0 ? '#F44336' : '#4CAF50' },
            { label: 'IROP Potential',   value: highRiskCount,        color: highRiskCount > 0 ? '#FF9800' : '#4CAF50' },
            { label: 'High Risk PAX',    value: tilesState.reduce((n, t) => n + (t.nearby_high_risk || 0), 0), color: '#9C27B0' },
          ].map(k => (
            <div key={k.label} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold tabular-nums" style={{ color: k.color }}>{k.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-2.5 mb-4 flex items-center gap-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">IROP filter:</span>
          {['all', 'high', 'moderate'].map(f => (
            <button
              key={f}
              onClick={() => setFilterIrop(f)}
              className={`text-xs px-3 py-1 rounded-full border font-medium ${
                filterIrop === f
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f === 'all' ? 'All venues' : `${f} risk`}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-400">{sortedTiles.length} of {tilesState.length} venues</span>
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="mb-4">
            <TileDetailPanel tile={selected} onClose={() => setSelected(null)} />
          </div>
        )}

        {/* Tile grid */}
        {sortedTiles.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-400 text-sm">No active venue anchors — run ChannelSensorSnapshotJob to populate data.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortedTiles.map(tile => (
              <VenueTile
                key={tile.anchor_id}
                tile={tile}
                onSelect={t => setSelected(selected?.anchor_id === t.anchor_id ? null : t)}
              />
            ))}
          </div>
        )}

        {/* Architecture note */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-700">
          <p className="font-semibold mb-0.5">📡 channelSensor ground truth (Phase 2A live)</p>
          <p>
            Data sourced from <code>UnifiedApi::ChannelSensorService.build_offer_snapshot</code> via
            PostgreSQL aggregates (no PII). <code>ChannelSensorSnapshotJob</code> refreshes every 15min.
            For teleportation scenarios (multi-sensePoint MSUG) see <code>SENSEPOINT_ARCHITECTURE.md §9.1</code>.
            Real-time CHANNEL_SENSOR_UPDATE events delivered via OpsMonitorChannel (ActionCable).
          </p>
        </div>

        {/* Nav */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/node_heat_map" className="text-blue-600 hover:underline">Node Heat Map</Link>
          <Link href="/admin/cloudtraq_gds/high_risk" className="text-blue-600 hover:underline">High Risk PAX</Link>
        </div>

      </div>
    </div>
  )
}
